import { useState, useEffect } from "react"
import { DEFAULT_ANALOG_CLOCK_SECONDS_COLOR } from "../lib/useBranding"

const CX = 24
const CY = 24
const R = 22

/** Returns rotation in degrees: 0 = 12 o'clock, 90 = 3, etc. */
function getRotation(now: Date, unit: "hour" | "minute" | "second"): number {
  const h = now.getHours() % 12
  const m = now.getMinutes()
  const s = now.getSeconds()
  const ms = now.getMilliseconds()
  switch (unit) {
    case "hour":
      return (h + m / 60 + s / 3600) * 30
    case "minute":
      return (m + s / 60 + ms / 60000) * 6
    case "second":
      return (s + ms / 1000) * 6
    default:
      return 0
  }
}

/** Light gray like Manifold border (stone-500 ~40%). */
const DEFAULT_ANALOG_CLOCK_OUTLINE_COLOR = "rgba(120,113,108,0.45)"

/**
 * Build tick mark configurations.
 * All ticks use rotate(deg CX CY) on a fixed upward line — keeps them
 * fully inset from the circle stroke, never intersecting the outline.
 *
 * Inset from outer edge: major ticks start at R-4 (y1=4, y2=8 in SVG space),
 * minor ticks start at R-3 (y1=5, y2=8).
 */
function buildTicks(count: 0 | 4 | 8 | 12): Array<{ deg: number; major: boolean }> {
  if (count === 0) return []
  if (count === 4) {
    return [0, 90, 180, 270].map((deg) => ({ deg, major: true }))
  }
  if (count === 8) {
    return [0, 45, 90, 135, 180, 225, 270, 315].map((deg) => ({
      deg,
      major: deg % 90 === 0,
    }))
  }
  // 12: every 30°; major at quarters
  return Array.from({ length: 12 }, (_, i) => ({
    deg: i * 30,
    major: i % 3 === 0,
  }))
}

/** Minimal analog clock: dark face, white dials, configurable seconds hand, outline, and tick count. */
export function AnalogClock({
  size = 56,
  secondsHandColor,
  outlineColor,
  fluidSeconds = false,
  tickMarks = 12,
  className = "",
}: {
  size?: number
  /** CSS color for seconds hand (from portal_branding.analog_clock_seconds_color). */
  secondsHandColor?: string | null
  /** CSS color for dial circle stroke. Defaults to light gray (Manifold-style). */
  outlineColor?: string | null
  /** If true, seconds hand moves smoothly; otherwise ticks every second. */
  fluidSeconds?: boolean
  /** Number of tick marks to show: 0 = none, 4 = quarters, 8 = eighths, 12 = all. Default 12. */
  tickMarks?: 0 | 4 | 8 | 12
  className?: string
}) {
  const [now, setNow] = useState(() => new Date())
  const color = secondsHandColor?.trim() || DEFAULT_ANALOG_CLOCK_SECONDS_COLOR
  const outline = outlineColor?.trim() || DEFAULT_ANALOG_CLOCK_OUTLINE_COLOR
  const ticks = buildTicks(tickMarks)

  useEffect(() => {
    if (fluidSeconds) {
      let raf = 0
      const tick = () => {
        setNow(new Date())
        raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)
      return () => cancelAnimationFrame(raf)
    }
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [fluidSeconds])

  const hRot = getRotation(now, "hour")
  const mRot = getRotation(now, "minute")
  const sRot = getRotation(now, "second")

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={`shrink-0 ${className}`}
      aria-hidden
    >
      {/* Dial face */}
      <circle cx={CX} cy={CY} r={R} fill="#1c1917" stroke={outline} strokeWidth="1" />

      {/* Tick marks — rotate around center; y coords are in dial-face space so they
          never reach the outline (y1=4 = R-18 inward, y2=8 = R-14 inward from top) */}
      {ticks.map(({ deg, major }) => (
        <line
          key={deg}
          x1={CX}
          y1={major ? 4 : 5}
          x2={CX}
          y2={major ? 8 : 8}
          stroke={major ? "rgba(255,255,255,0.70)" : "rgba(255,255,255,0.28)"}
          strokeWidth={major ? 1.2 : 0.9}
          strokeLinecap="round"
          transform={`rotate(${deg} ${CX} ${CY})`}
        />
      ))}

      {/* Hour hand */}
      <line
        x1={CX}
        y1={CY}
        x2={CX}
        y2={CY - R * 0.42}
        stroke="rgba(255,255,255,0.9)"
        strokeWidth="2"
        strokeLinecap="round"
        transform={`rotate(${hRot} ${CX} ${CY})`}
      />

      {/* Minute hand */}
      <line
        x1={CX}
        y1={CY}
        x2={CX}
        y2={CY - R * 0.66}
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="1.4"
        strokeLinecap="round"
        transform={`rotate(${mRot} ${CX} ${CY})`}
      />

      {/* Seconds hand — color from branding */}
      <line
        x1={CX}
        y1={CY + 3}
        x2={CX}
        y2={CY - R * 0.82}
        stroke={color}
        strokeWidth="0.9"
        strokeLinecap="round"
        transform={`rotate(${sRot} ${CX} ${CY})`}
      />

      {/* Center dot */}
      <circle cx={CX} cy={CY} r="1.5" fill="white" />
    </svg>
  )
}
